import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  Box,
  Paper,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  EventNote as EventNoteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

interface RecurringShiftDeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (choice: 'this' | 'future') => void;
  shiftInfo?: {
    title?: string;
    startDateTime?: string;
    isRecurrenceParent?: boolean;
    hasChildren?: boolean;
  };
}

const RecurringShiftDeleteModal: React.FC<RecurringShiftDeleteModalProps> = ({
  open,
  onClose,
  onConfirm,
  shiftInfo
}) => {
  const [selectedOption, setSelectedOption] = React.useState<'this' | 'future'>('this');

  const handleConfirm = () => {
    onConfirm(selectedOption);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      setSelectedOption('this'); // Reset to default when modal opens
    }
  }, [open]);

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return '';
    return new Date(dateTime).toLocaleString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <DeleteIcon color="error" />
          <Typography variant="h6" fontWeight="600">
            Delete Recurring Shift
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {/* Shift Info Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            bgcolor: 'background.default', 
            p: 2, 
            mb: 3, 
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Selected Shift
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <EventNoteIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight="500">
              {shiftInfo?.title || 'Shift'}
            </Typography>
          </Box>
          {shiftInfo?.startDateTime && (
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(shiftInfo.startDateTime)}
            </Typography>
          )}
        </Paper>

        <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
          This shift is part of a recurring series. Choose how you want to delete it:
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value as 'this' | 'future')}
          >
            {/* Delete Only This Occurrence */}
            <Paper 
              elevation={0}
              sx={{
                border: '2px solid',
                borderColor: selectedOption === 'this' ? 'error.main' : 'divider',
                borderRadius: 2,
                p: 2,
                mb: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'error.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => setSelectedOption('this')}
            >
              <FormControlLabel
                value="this"
                control={<Radio color="error" />}
                label={
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <DeleteIcon fontSize="small" color="error" />
                      <Typography variant="subtitle1" fontWeight="600">
                        Delete only this occurrence
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Only this specific shift will be deleted. Other shifts in the series remain unchanged.
                    </Typography>
                  </Box>
                }
                sx={{ 
                  m: 0, 
                  alignItems: 'flex-start',
                  '& .MuiRadio-root': { pt: 0 }
                }}
              />
            </Paper>

            {/* Delete This and Future Occurrences */}
            <Paper 
              elevation={0}
              sx={{
                border: '2px solid',
                borderColor: selectedOption === 'future' ? 'error.main' : 'divider',
                borderRadius: 2,
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'error.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => setSelectedOption('future')}
            >
              <FormControlLabel
                value="future"
                control={<Radio color="error" />}
                label={
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <DeleteSweepIcon fontSize="small" color="error" />
                      <Typography variant="subtitle1" fontWeight="600">
                        Delete this and all future occurrences
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      This shift and all future shifts in the recurring series will be permanently deleted.
                    </Typography>
                  </Box>
                }
                sx={{ 
                  m: 0, 
                  alignItems: 'flex-start',
                  '& .MuiRadio-root': { pt: 0 }
                }}
              />
            </Paper>
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'warning.light', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'warning.main'
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <WarningIcon fontSize="small" color="warning" />
            <Typography variant="subtitle2" color="warning.dark" fontWeight="600">
              Warning
            </Typography>
          </Box>
          <Typography variant="body2" color="warning.dark">
            This action cannot be undone. Past occurrences of the recurring series will not be affected.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleCancel}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          color="error"
          size="large"
          sx={{ minWidth: 120 }}
          startIcon={<DeleteIcon />}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecurringShiftDeleteModal;